<template>
    <div class="google-autocomplete__container">
        <label
            v-if="googleAutocompleteLabel"
            class="ls-label ls-margin--top"
            :for="googleAutocompleteId"
        >
            {{ googleAutocompleteLabel }}
        </label>
        <div class="ls-input-group">
            <div
                v-if="address.showStandaloneStreetNumber"
                class="ls-input__wrapper google-autocomplete__staipt"
            >
                <input
                    id="staipt"
                    type="text"
                    autocomplete="new-password"
                    class="ls-input ls-border-right-radius-none ls-border-right-none"
                    name="staipt"
                    :value="address.manualStreetNumber"
                    @input="onStreetNumberInput($event)"
                />
                <div
                    v-if="isValid === false && address.manualStreetNumber < 1"
                    class="google-autocomplete__staipt-tooltip ls-bold"
                    data-balloon-visible
                    data-balloon-length="fit"
                    data-balloon-pos="down"
                    aria-label="Enter your street number, please."
                ></div>
            </div>

            <div class="ls-input__wrapper">
                <ValidationProvider
                    ref="address"
                    v-slot="{ classes }"
                    :rules="{
                        required: true,
                        hasStreetNumber: {
                            address,
                        },
                        hasPlaceId: {
                            address,
                        },
                    }"
                >
                    <span class="ls-input__icon" :class="classes" aria-hidden="true">
                        <i class="fa fa-map-marker fa-fw fa-lg"></i>
                    </span>
                    <input
                        :id="googleAutocompleteId"
                        type="text"
                        autocomplete="new-password"
                        class="ls-input"
                        :class="{
                            ...classes,
                            'ls-border-left-radius-none': address.showStandaloneStreetNumber,
                            'ls-border-bottom-radius-none': showPredictions,
                        }"
                        :name="googleAutocompleteId"
                        :placeholder="googleAutocompletePlaceholder"
                        :value="value"
                        @input="onAddressInput($event)"
                        @blur="onInputBlur($event)"
                        @keyup.up="onInputUp($event)"
                        @keyup.down="onInputDown($event)"
                        @keyup.enter="onInputEnter($event)"
                    />
                </ValidationProvider>

                <div v-if="showPredictions" class="google-autocomplete__list-container">
                    <div class="google-autocomplete__list">
                        <a
                            v-for="prediction in autocompletePredictions"
                            :key="prediction.place_id"
                            class="google-autocomplete__list-item ls-truncate"
                            :class="{
                                'google-autocomplete__highlighted-item':
                                    prediction === highlightedPrediction,
                            }"
                            @mouseover="highlightedPrediction = prediction"
                            @click="selectPrediction(prediction)"
                        >
                            <i
                                class="fa fa-lg fa-map-marker"
                                :class="{
                                    'ls-color--gray--x-light': prediction !== highlightedPrediction,
                                    'ls-color--red--color-dodge':
                                        prediction === highlightedPrediction,
                                }"
                            ></i
                            >&nbsp;&nbsp;&nbsp;<span>{{ prediction.description }}</span>
                        </a>
                        <div class="google-autocomplete__google-logo">
                            <img
                                src="//maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
                                height="12"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import * as types from '@/store/types';
import lsGoogleService from '@/services/google/google.service';
import { lsErrorService } from 'ls-vue-common';

export default {
    name: 'GoogleAutocomplete',
    props: {
        value: {
            type: String,
            default: null,
        }, // v-model provided, should be googlePlace.formatted_address
        googleAutocompleteLabel: {
            type: String,
            default: 'Your Address',
        },
        googleAutocompleteId: {
            type: String,
            default: 'staipt',
        },
        googleAutocompletePlaceholder: {
            default: null,
            type: String,
        },
    },
    data() {
        return {
            autocompletePredictions: [],
            selectedItemIndex: 0,
            highlightedPrediction: null,
            isValid: null,
        };
    },
    computed: {
        ...mapState(['address']),
        showPredictions() {
            return _.size(this.autocompletePredictions);
        },
    },
    methods: {
        onStreetNumberInput(evt) {
            const streetNumber = evt.target.value;
            this.updateStreetNumber(streetNumber);
            this.$refs.address.validate();
        },

        onAddressInput(evt) {
            const formatted_address = evt.target.value;
            // resets on input
            this.selectedItemIndex = 0;
            this.predictionSelected = false;
            this.getAddressPredictions(formatted_address);
            this.updateAddress({ formatted_address }); // dont emit input so we still update value with vuex
        },

        onInputBlur() {
            // race condition between blur and selecting an option in dropdown
            const WAIT_FOR_SELECTION_CLICK_DELAY = 200;
            setTimeout(() => {
                if (!this.predictionSelected && _.size(this.autocompletePredictions)) {
                    this.selectPrediction(_.head(this.autocompletePredictions));
                }
            }, WAIT_FOR_SELECTION_CLICK_DELAY);
        },

        onInputUp() {
            const predictionCount = _.size(this.autocompletePredictions);
            if (!predictionCount) {
                return;
            }
            // properly wrap up and down
            if (this.selectedItemIndex === 0) {
                this.selectedItemIndex = predictionCount - 1;
            } else {
                this.selectedItemIndex -= 1;
            }
            this.highlightedPrediction = this.autocompletePredictions[this.selectedItemIndex];
        },

        onInputDown() {
            const predictionCount = _.size(this.autocompletePredictions);
            if (!predictionCount) {
                return;
            }
            // properly wrap up and down
            if (this.selectedItemIndex === predictionCount - 1) {
                this.selectedItemIndex = 0;
            } else {
                this.selectedItemIndex += 1;
            }
            this.highlightedPrediction = this.autocompletePredictions[this.selectedItemIndex];
        },

        onInputEnter() {
            if (this.highlightedPrediction && this.showPredictions) {
                this.selectPrediction(this.highlightedPrediction);
            }
        },

        getAddressPredictions(input) {
            if (!input) {
                return;
            }

            lsGoogleService
                .getPlacePredictions({
                    input,
                    componentRestrictions: { country: 'us' },
                    types: ['address'],
                })
                .then((predictions) => {
                    this.autocompletePredictions = predictions;
                    this.highlightedPrediction = _.head(predictions);
                })
                .catch((error) => {
                    lsErrorService.log({ error });
                });
        },

        selectPrediction({ place_id }) {
            this.predictionSelected = true;
            this.autocompletePredictions = [];
            if (place_id) {
                lsGoogleService
                    .getDetails({
                        placeId: place_id,
                    })
                    .then((place) => {
                        this.googlePredictionSelected(place);
                    })
                    .catch((error) => {
                        lsErrorService.log({ error });
                    })
                    .then(() => {
                        this.validateAddress();
                    });
            }
        },

        validateAddress() {
            this.$refs.address.validate().then(({ valid }) => {
                this.isValid = valid;
            });
        },

        ...mapActions({
            updateAddress: types.ADDRESS__UPDATE_ADDRESS,
            googlePredictionSelected: types.ADDRESS__GOOGLE_PREDICTION_SELECTED,
            updateStreetNumber: types.ADDRESS__UPDATE_MANUAL_STREET_NUMBER,
            updateShowStreetNumber: types.ADDRESS__UPDATE_SHOW_STANDALONE_STREET_NUMBER,
        }),
    },

    mounted() {
        // only get predictions when we have a value and dont already have a google place id
        if (this.value && !this.address.googlePlace.place_id) {
            lsGoogleService
                .getPlacePredictions({
                    input: this.value,
                })
                .then((predictions) => {
                    if (_.size(predictions)) {
                        this.selectPrediction(_.head(predictions));
                    } else {
                        this.validateAddress();
                    }
                })
                .catch((error) => {
                    lsErrorService.log({ error });
                });
        }
    },
};
// inspiration from https://github.com/olefirenko/vue-google-autocomplete
</script>

<style lang="scss">
.google-autocomplete__container {
    position: relative;
}

.google-autocomplete__staipt {
    position: relative; // for tooltip placement
    min-width: 120px;
    max-width: 120px;
}

.google-autocomplete__staipt-tooltip {
    position: absolute;
    width: 100%;
    text-align: center;
}

.google-autocomplete__list-container {
    position: relative;
    bottom: 0;
}

.google-autocomplete__list {
    position: absolute; // to avoid pushing content down
    z-index: z-index(google-autocomplete);
    top: 0;
    left: 0;
    width: 100%;

    border: 1px solid palette(gray, x-light);
    border-top: none; // to align top-edge with bottom-edge of ls-input
    background-color: palette(white);
    box-shadow: 0 rem(-3) rem(4) (-(rem(-3))) rgba(palette(black), 0.8);
}

.google-autocomplete__list-item {
    cursor: pointer;
    display: block !important; // override .ls-truncate inline-block
    width: 100%;
    padding: rem(-4);
    font-size: rem(-1);
    border-bottom: 1px solid palette(gray, x-light);
}

.google-autocomplete__highlighted-item {
    background-color: palette(blue, xx-light);
}

.google-autocomplete__google-logo {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: em(-3);
    font-size: em(-3);
}
.themed-app {
    .google-autocomplete__list {
        border-color: $theme-color-gray;
        background-color: $theme-color-white;
        box-shadow: 0 rem(-3) rem(4) (-(rem(-3))) rgba($theme-color-black, 0.8);
    }

    .google-autocomplete__list-item {
        border-color: $theme-color-gray;
    }

    .google-autocomplete__highlighted-item {
        background-color: $theme-color-blue-light;
    }
}
</style>
